import React, { useState, useCallback } from 'react'
import Grid from '@material-ui/core/Grid'
import PropTypes from 'prop-types'
import SydButton from '../../../../../../../commonDesign/Button'
import SydModalActions from '../../../../../../../commonDesign/SydModal/SydModalActions'
import SydLabel from '../../../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../../../commonDesign/SydInput'
import SydSelect from '../../../../../../../commonDesign/SydSelect'

const presentationTypes = [
  { label: 'Data Grid', value: 'Data Grid' },
  { label: 'Presentation Table', value: 'Presentation Table' },
  { label: 'Hierarchical Table', value: 'Hierarchical Table' },
  { label: 'Hierarchical Grid', value: 'Hierarchical Grid' },
  { label: 'Metric', value: 'Metric' },
  { label: 'Chart', value: 'Chart' },
  { label: 'Scope Selector', value: 'Scope Selector' },
  { label: 'Hierarchical Pivot Table', value: 'Hierarchical Pivot Table' }
]

function generateLocalStorageKey () {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < 32; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

function AddPresentationForm ({ onCancel, onComplete }) {
  const [optionCode, setOptionCode] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [type, setType] = useState('Data Grid')
  const [description, setDescription] = useState('')
  const [error, setError] = useState(null)

  const isValid = optionCode.trim() && displayName.trim() && /^[a-z0-9-]+$/.test(optionCode.trim())

  const handleCreate = useCallback(() => {
    if (!isValid) return

    const codeValue = optionCode.trim()
    if (!/^[a-z0-9-]+$/.test(codeValue)) {
      setError('Option code must be lowercase letters, numbers, and hyphens only')
      return
    }

    setError(null)

    let config
    if (type === 'Metric') {
      config = {
        purposeCode: null,
        accessor: null,
        title: null,
        primaryFormat: null,
        additionalFormats: [],
        icon: null,
        footer: null,
        size: 'md',
        align: 'left'
      }
    } else if (type === 'Chart') {
      config = {
        layout: 'axis',
        purposeCode: null,
        height: 400,
        colorTheme: 'primary',
        xAxis: { type: 'category', accessor: null, format: null, name: null, position: 'bottom', boundaryGap: null },
        yAxes: [{ axisId: 'left', type: 'value', format: null, name: null, position: 'left', padFactor: 0 }],
        tooltip: { trigger: 'axis', valueFormat: null },
        series: []
      }
    } else if (type === 'Hierarchical Table') {
      config = {
        purposeCode: null,
        columns: [],
        hierarchy: {
          levels: [],
          defaultExpandDepth: 1,
          aggregation: { mode: 'none', columnAggregates: [], depthColumn: null },
          labelColumn: '_hierarchyLabel',
          leafLabelAccessor: null
        },
        footerPurposeCode: null,
        footerColumnMapping: [],
        defaultSort: [],
        export: { enabled: false, formats: [], defaultFileName: null }
      }
    } else if (type === 'Hierarchical Grid') {
      config = {
        columns: [],
        hierarchy: {
          levels: [],
          initialFetchDepth: 1,
          defaultExpandDepth: 0,
          labelColumn: '_hierarchyLabel',
          leafLabelAccessor: null
        },
        defaultSort: [],
        localStorageKey: generateLocalStorageKey(),
        variationGroups: ['default'],
        latchMode: 'selectable',
        enableHierarchicalFilter: true,
        export: { enabled: false, formats: [], defaultFileName: null, flattenOnExport: true }
      }
    } else if (type === 'Scope Selector') {
      config = {
        mode: 'membership',
        purposeCode: null,
        scopes: [],
        display: 'simple',
        multiSelect: false,
        hideAvatars: false,
        size: 'sm'
      }
    } else if (type === 'Hierarchical Pivot Table') {
      config = {
        purposeCode: null,
        columns: [],
        hierarchy: {
          levels: [],
          defaultExpandDepth: 1,
          labelColumn: '_hierarchyLabel',
          leafLabelAccessor: null
        },
        pivot: {
          pivotColumn: '',
          valueColumn: '',
          aggregateFunction: 'sum',
          showTotal: true,
          showAllocation: false,
          sortPivotColumns: 'alpha',
          enableColumnGrouping: false,
          groupColumn: null
        },
        footerPurposeCode: null,
        footerColumnMapping: [],
        defaultSort: [],
        export: { enabled: false, formats: [], defaultFileName: null }
      }
    } else {
      config = {
        purposeCode: null,
        columns: [],
        footerPurposeCode: null,
        footerColumnMapping: [],
        defaultSort: [],
        localStorageKey: generateLocalStorageKey(),
        variationGroups: ['default'],
        topN: null
      }
    }

    const newOption = {
      displayName: displayName.trim(),
      description: description.trim() || null,
      type,
      config
    }

    onComplete({ optionCode: codeValue, optionData: newOption })
  }, [optionCode, displayName, type, description, isValid, onComplete])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <SydLabel required label='Option Code' helperText='Lowercase, numbers, hyphens (e.g., default-grid)'>
          <SydInput
            value={optionCode}
            onChange={(e) => setOptionCode(e.target.value)}
            placeholder='default-grid'
          />
        </SydLabel>
      </Grid>

      <Grid item xs={12} md={6}>
        <SydLabel required label='Display Name' helperText='Human-readable name'>
          <SydInput
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder='Default Data Grid'
          />
        </SydLabel>
      </Grid>

      <Grid item xs={12}>
        <SydLabel required label='Presentation Type'>
          <SydSelect
            options={presentationTypes}
            value={type}
            onChange={(e) => setType(e.target.value)}
          />
        </SydLabel>
      </Grid>

      <Grid item xs={12}>
        <SydLabel label='Description' helperText='Optional description of this presentation'>
          <SydInput
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            placeholder='Standard data grid with paging and filtering'
          />
        </SydLabel>
      </Grid>

      <Grid item xs={12}>
        {error ? <div style={{ color: 'red', marginBottom: '8px' }}>{error}</div> : null}
        <SydModalActions>
          <SydButton variant='ghost' size='md' onClick={onCancel}>
            Cancel
          </SydButton>
          <SydButton
            disabled={!isValid}
            variant='primary'
            size='md'
            onClick={handleCreate}
          >
            Create Presentation
          </SydButton>
        </SydModalActions>
      </Grid>
    </Grid>
  )
}

AddPresentationForm.propTypes = {
  onComplete: PropTypes.func,
  onCancel: PropTypes.func
}

export default AddPresentationForm
